import { AMADEUS_API_KEY, AMADEUS_API_SECRET, AMADEUS_TOKEN_URL, AMADEUS_FLIGHT_URL, AMADEUS_HOTEL_URL, AMADEUS_FLIGHT_PRICING_URL, AMADEUS_AIRPORT_FACILITIES_URL, AMADEUS_FLIGHT_STATUS_URL, AMADEUS_HOTEL_SEARCH_URL, AMADEUS_HOTEL_OFFERS_URL, AMADEUS_HOTEL_IMAGES_URL } from '@/config/api';
import { AMADEUS_API } from '@/config/api';

// Comprehensive mapping of Indian cities to IATA codes
const CITY_TO_IATA: { [key: string]: string } = {
  // Major Cities
  'mumbai': 'BOM',
  'delhi': 'DEL',
  'bangalore': 'BLR',
  'bengaluru': 'BLR',
  'hyderabad': 'HYD',
  'chennai': 'MAA',
  'kolkata': 'CCU',
  'ahmedabad': 'AMD',
  'pune': 'PNQ',
  'jaipur': 'JAI',
  'goa': 'GOI',
  'surat': 'STV',
  'lucknow': 'LKO',
  'kochi': 'COK',
  'nagpur': 'NAG',
  'indore': 'IDR',
  'chandigarh': 'IXC',
  'visakhapatnam': 'VTZ',
  'bhubaneswar': 'BBI',
  'patna': 'PAT',
  'guwahati': 'GAU',
  'thiruvananthapuram': 'TRV',
  'trivandrum': 'TRV',
  'varanasi': 'VNS',
  'coimbatore': 'CJB',
  'udaipur': 'UDR',
  'amritsar': 'ATQ',
  'bhopal': 'BHO',
  'madurai': 'IXM',
  'raipur': 'RPR',
  'agra': 'AGR',
  // Add more cities as needed
};

// Token storage
let tokenData: {
  accessToken: string;
  expiresAt: number;
} | null = null;

// Function to check if token is expired
export function isTokenExpired(): boolean {
  if (!tokenData) return true;
  // Check if token expires in the next minute (buffer time)
  return Date.now() >= tokenData.expiresAt - 60000;
}

// Function to get access token
export async function getAccessToken(): Promise<string> {
  try {
    const response = await fetch(AMADEUS_API.TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        'grant_type': 'client_credentials',
        'client_id': AMADEUS_API.CLIENT_ID,
        'client_secret': AMADEUS_API.CLIENT_SECRET,
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to get access token: ${response.status}`);
    }

    const data = await response.json();
    
    // Store token with expiry time (30 minutes from now)
    tokenData = {
      accessToken: data.access_token,
      expiresAt: Date.now() + (data.expires_in * 1000)
    };

    return data.access_token;
  } catch (error) {
    console.error('Error getting access token:', error);
    throw error;
  }
}

// Function to get current token or refresh if expired
async function ensureValidToken(): Promise<string> {
  if (isTokenExpired()) {
    return await getAccessToken();
  }
  return tokenData!.accessToken;
}

// Function to get time until token expiry in milliseconds
export function getTokenExpiryTime(): number | null {
  if (!tokenData) return null;
  return Math.max(0, tokenData.expiresAt - Date.now());
}

// Function to convert city name to IATA code
export function cityToIATACode(city: string): { code: string; correctedCity?: string; error?: string } {
  try {
    const normalizedCity = city.toLowerCase().trim();
    
    // Direct lookup
    if (CITY_TO_IATA[normalizedCity]) {
      return { code: CITY_TO_IATA[normalizedCity] };
    }

    // Try to find closest match
    const cities = Object.keys(CITY_TO_IATA);
    const matches = cities.filter(c => 
      c.includes(normalizedCity) || normalizedCity.includes(c)
    );

    if (matches.length > 0) {
      const closestMatch = matches[0];
      return { 
        code: CITY_TO_IATA[closestMatch],
        correctedCity: closestMatch
      };
    }

    throw new Error(`Could not find IATA code for city: ${city}`);
  } catch (error) {
    return {
      code: '',
      error: `Invalid city: ${city}. Please enter a major Indian city.`
    };
  }
}

// Export available cities for UI
export const AVAILABLE_CITIES = Object.keys(CITY_TO_IATA).sort();

// Interfaces for API responses
interface FlightOffer {
  id: string;
  itineraries: Array<{
    segments: Array<{
      departure: {
        iataCode: string;
        terminal?: string;
        at: string;
      };
      arrival: {
        iataCode: string;
        terminal?: string;
        at: string;
      };
      carrierCode: string;
      number: string;
      aircraft: {
        code: string;
      };
      duration: string;
      numberOfStops: number;
    }>;
  }>;
  price: {
    currency: string;
    total: string;
    base: string;
    fees: Array<{
      amount: string;
      type: string;
    }>;
  };
  travelerPricings: Array<{
    travelerId: string;
    fareOption: string;
    travelerType: string;
    price: {
      currency: string;
      total: string;
    };
  }>;
}

interface FlightPricing {
  flightOffers: FlightOffer[];
  bookingRequirements: {
    invoiceAddressRequired: boolean;
    emailAddressRequired: boolean;
    phoneCountryCodeRequired: boolean;
    postalCodeRequired: boolean;
    travelerRequirements: Array<{
      travelerId: string;
      documentRequired: boolean;
      documentTypes: string[];
    }>;
  };
}

interface AirportFacility {
  type: string;
  name: string;
  description?: string;
  location?: {
    terminal?: string;
    level?: string;
  };
  operatingHours?: {
    open: string;
    close: string;
    days: string[];
  };
}

interface FlightStatus {
  flightDesignator: {
    carrierCode: string;
    flightNumber: string;
  };
  flightPoints: Array<{
    iataCode: string;
    departure?: {
      timings: Array<{
        qualifier: string;
        value: string;
      }>;
    };
    arrival?: {
      timings: Array<{
        qualifier: string;
        value: string;
      }>;
    };
  }>;
  segments: Array<{
    boardingTerminal?: string;
    departureTerminal?: string;
    arrivalTerminal?: string;
    departure: {
      iataCode: string;
      scheduledTime: string;
      actualTime?: string;
      gate?: string;
    };
    arrival: {
      iataCode: string;
      scheduledTime: string;
      actualTime?: string;
      gate?: string;
    };
    marketingCarrier: {
      icaoCode: string;
      flightNumber: string;
    };
  }>;
}

interface HotelImage {
  uri: string;
  category: string;
}

interface HotelOffer {
  id: string;
  checkInDate: string;
  checkOutDate: string;
  roomQuantity: number;
  rateCode: string;
  rateFamilyEstimated: {
    code: string;
    type: string;
  };
  price: {
    currency: string;
    total: string;
    variations: {
      changes: Array<{
        startDate: string;
        endDate: string;
        total: string;
      }>;
    };
  };
  policies: {
    cancellation: {
      description?: string;
      type: string;
      amount?: string;
      deadline?: string;
    };
    paymentType: string;
    guarantee?: {
      acceptedPayments: {
        creditCards: string[];
        methods: string[];
      };
    };
  };
  room: {
    type: string;
    description: {
      text: string;
      lang: string;
    };
  };
}

interface HotelSearchResponse {
  data: Array<{
    hotelId: string;
    name: string;
    rating: string;
    description?: {
      text: string;
      lang: string;
    };
    amenities: string[];
    contact: {
      phone?: string;
      email?: string;
    };
    address: {
      lines: string[];
      cityName: string;
      countryCode: string;
      postalCode?: string;
    };
    location: {
      latitude: number;
      longitude: number;
    };
    images?: HotelImage[];
    offers?: HotelOffer[];
  }>;
}

// Function to get flight pricing details
async function getFlightPricing(flightOfferId: string): Promise<FlightPricing> {
  try {
    const token = await ensureValidToken();
    
    const response = await fetch(AMADEUS_FLIGHT_PRICING_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        data: {
          type: 'flight-offers-pricing',
          flightOffers: [{ id: flightOfferId }]
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Flight pricing failed: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error getting flight pricing:', error);
    throw error;
  }
}

// Function to get airport facilities
async function getAirportFacilities(airportCode: string): Promise<AirportFacility[]> {
  try {
    const token = await ensureValidToken();
    
    const url = AMADEUS_AIRPORT_FACILITIES_URL.replace('{airportCode}', airportCode);
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Airport facilities search failed: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error getting airport facilities:', error);
    throw error;
  }
}

// Function to get flight status
async function getFlightStatus(flightNumber: string): Promise<FlightStatus> {
  try {
    const token = await ensureValidToken();
    
    const url = AMADEUS_FLIGHT_STATUS_URL.replace('{flightNumber}', flightNumber);
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Flight status search failed: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error getting flight status:', error);
    throw error;
  }
}

// Update the searchFlights function to include comprehensive information
export async function searchFlights(
  originLocationCode: string,
  destinationLocationCode: string,
  departureDate: string,
  returnDate?: string,
  adults: number = 1,
  cabinClass: string = 'ECONOMY'
) {
  try {
    // Validate required fields
    if (!originLocationCode || !destinationLocationCode || !departureDate) {
      throw new Error('Missing required fields for flight search');
    }

    // Convert city names to IATA codes
    let origin: string;
    let destination: string;

    try {
      origin = cityToIATACode(originLocationCode).code;
    } catch (error) {
      throw new Error(`Invalid origin city: ${originLocationCode}. ${(error as Error).message}`);
    }

    try {
      destination = cityToIATACode(destinationLocationCode).code;
    } catch (error) {
      throw new Error(`Invalid destination city: ${destinationLocationCode}. ${(error as Error).message}`);
    }

    // Validate date format (YYYY-MM-DD)
    const validateDate = (date: string) => {
      const datePattern = /^\d{4}-\d{2}-\d{2}$/;
      if (!datePattern.test(date)) {
        throw new Error(`Invalid date format: ${date}. Required format: YYYY-MM-DD`);
      }
      return date;
    };

    const depDate = validateDate(departureDate);
    const retDate = returnDate ? validateDate(returnDate) : undefined;

    const token = await ensureValidToken();
    
    // Construct URL with required parameters
    let url = `${AMADEUS_FLIGHT_URL}?originLocationCode=${origin}&destinationLocationCode=${destination}&departureDate=${depDate}&adults=${adults}&currencyCode=INR&max=5`;
    
    if (retDate) {
      url += `&returnDate=${retDate}`;
    }

    if (cabinClass) {
      url += `&travelClass=${cabinClass}`;
    }

    console.log('Searching flights with URL:', url);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Flight search failed: ${response.status} - ${JSON.stringify(errorData)}`);
    }
    
    const flightOffers = await response.json();

    // Add a status field to each flight offer
    const enhancedFlightOffers = flightOffers.data.map((offer: FlightOffer) => ({
      ...offer,
      status: 'Scheduled', // Default status
      lastUpdated: new Date().toLocaleTimeString() // Current time
    }));

    return {
      data: enhancedFlightOffers,
      dictionaries: flightOffers.dictionaries
    };
  } catch (error) {
    console.error('Error searching flights:', error);
    throw error;
  }
}

// Function to get hotel images
async function getHotelImages(hotelId: string): Promise<HotelImage[]> {
  try {
    const token = await ensureValidToken();
    const url = AMADEUS_HOTEL_IMAGES_URL.replace('{hotelId}', hotelId);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      console.warn(`Failed to fetch images for hotel ${hotelId}`);
      return [];
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error getting hotel images:', error);
    return [];
  }
}

// Function to get hotel offers
async function getHotelOffers(
  hotelId: string,
  checkInDate: string,
  checkOutDate: string,
  adults: number,
  currencyCode: string = 'INR'
): Promise<HotelOffer[]> {
  try {
    const token = await ensureValidToken();
    
    const url = new URL(AMADEUS_HOTEL_OFFERS_URL);
    url.searchParams.append('hotelId', hotelId);
    url.searchParams.append('checkInDate', checkInDate);
    url.searchParams.append('checkOutDate', checkOutDate);
    url.searchParams.append('adults', adults.toString());
    url.searchParams.append('currencyCode', currencyCode);
    
    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Hotel offers search failed: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Error getting hotel offers:', error);
    throw error;
  }
}

// Updated function to search for hotels
export async function searchHotels(
  cityName: string,
  checkInDate: string,
  checkOutDate: string,
  adults: number = 1,
  options: {
    radius?: number;
    starRating?: string;
    maxRate?: number;
    currencyCode?: string;
  } = {}
): Promise<HotelSearchResponse> {
  try {
    const token = await ensureValidToken();
    
    // Convert city name to IATA code
    let iataCode: string;
    try {
      iataCode = cityToIATACode(cityName).code;
    } catch (error) {
      console.error('Error converting city to IATA code:', error);
      throw new Error(`Invalid city name: ${cityName}. Please enter a valid Indian city.`);
    }

    const url = AMADEUS_HOTEL_SEARCH_URL.replace('{cityCode}', iataCode);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Hotel search failed: ${response.status} - ${JSON.stringify(errorData)}`);
    }
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error searching hotels:', error);
    throw error;
  }
}